import { existsSync, mkdirSync, readFileSync, writeFileSync } from "node:fs";
import { homedir } from "node:os";
import { join } from "node:path";
import { brandSkillContentFor, command } from "./brand.js";
import { applyInstallIdentity, installedSourceKey } from "./skill-identity.js";
import {
  detectAgent,
  formatInstallWriteFailure,
  isAgentType,
  isValidInstallName,
  resolveInstallDir,
  SUPPORTED_AGENTS,
} from "./skill-install-helpers.js";
import { SKILL_TEMPLATES } from "./skill-templates/index.js";
import { knownVersions, runDiff, runVersions } from "./skill-version-commands.js";
import type { AgentType } from "./skill-install-helpers.js";

export { applyInstallIdentity, installedSourceKey, SKILL_SOURCE_KEY } from "./skill-identity.js";
export {
  detectAgent,
  formatInstallWriteFailure,
  isInstallPermissionError,
  isValidInstallName,
  resolveInstallDir,
} from "./skill-install-helpers.js";
export type { AgentType, InstallWriteFailureContext } from "./skill-install-helpers.js";

/**
 * Render a skill template for the active brand. Install/output go through here
 * so a `cofactor` binary writes cofactor-branded skill files while `canary`
 * stays byte-for-byte identical. Authored-content tooling (`diff`, `versions`)
 * stays raw so it compares the source, not the per-brand presentation.
 */
function renderForActiveBrand(template: { render: () => string }): string {
  return brandSkillContentFor(template.render(), command);
}

function printSkillHelp() {
  const lines = [
    "Usage:",
    "  canary skill <name>                          Output skill template to stdout",
    "  canary skill install <name>                  Install skill (auto-detects agent)",
    "  canary skill install <name> --agent codex    Install for a specific agent",
    "  canary skill install <name> --personal       Install to ~/.<agent>/skills/",
    "  canary skill install <name> --name <as>      Install under a custom skill name",
    "  canary skill install <name> --force          Overwrite a name held by another skill",
    "  canary skill list                            List available skill templates",
    "  canary skill sync                            Print manifest + instructions for the",
    "                                               agent to version-compare and update",
    "                                               installed skills",
    "  canary skill diff <name> [--from V] [--to V] Unified diff between two versions of",
    "                                               a skill (defaults: previous → current)",
    "  canary skill versions <name>                 List all known versions of a skill",
    "",
    "Agent auto-detection:",
    "  The install command detects which AI agent is running and writes the",
    "  SKILL.md to the correct location:",
    "",
    "  Agent      Project path                   Personal path",
    "  ─────      ────────────                   ─────────────",
    "  claude     .claude/skills/<name>/         ~/.claude/skills/<name>/",
    "  codex      .codex/skills/<name>/          ~/.codex/skills/<name>/",
    "  copilot    .github/skills/<name>/         ~/.copilot/skills/<name>/",
    "",
    "  Detection checks (in order):",
    "    1. CLAUDE env var              → claude",
    "    2. CODEX_* env vars            → codex",
    "    3. GITHUB_COPILOT env var      → copilot",
    "    4. .claude/.codex/.github dirs → whichever single one exists",
    "    5. If ambiguous, use --agent to specify explicitly",
    "",
    "Custom skill names (--name):",
    "  By default a skill installs under its template name. Pass --name to install",
    "  it under a name of your choosing — useful when you want every Canary-provided",
    "  skill namespaced (e.g. canary-browser-testing) to avoid colliding with your",
    "  own skills. --name sets both the install directory and the SKILL.md",
    "  frontmatter name: (agents require those to match), and records the source",
    "  template in a skill-source: field so `sync` and `diff` keep tracking updates",
    "  even under the renamed install.",
    "",
    "    canary skill install browser-testing --name canary-browser-testing",
    "",
    "  Installing a different skill onto an existing custom name is refused unless",
    "  you pass --force. When an agent installs skills on your behalf, it should",
    "  follow your naming convention.",
    "",
    "Available templates:",
  ];

  for (const [key, template] of Object.entries(SKILL_TEMPLATES)) {
    lines.push(`  ${key.padEnd(24)} v${template.version.padEnd(7)} ${template.description}`);
  }

  console.log(lines.join("\n"));
}

function runSync() {
  const manifest = Object.entries(SKILL_TEMPLATES).map(([key, t]) => ({
    name: key,
    version: t.version,
    description: t.description,
    knownVersions: knownVersions(key),
    installedPaths: {
      claudeProject: `.claude/skills/${key}/SKILL.md`,
      claudePersonal: join(homedir(), ".claude", "skills", key, "SKILL.md"),
      codexProject: `.codex/skills/${key}/SKILL.md`,
      codexPersonal: join(homedir(), ".codex", "skills", key, "SKILL.md"),
      copilotProject: `.github/skills/${key}/SKILL.md`,
      copilotPersonal: join(homedir(), ".copilot", "skills", key, "SKILL.md"),
    },
  }));

  const instructions = `# Canary Skill Sync — Instructions for the Agent

You are an AI coding agent (Claude, Codex, Copilot, etc.). The user ran \`canary skill sync\`
to bring their installed Canary skill templates up to date. Follow these steps:

## Step 1 — Discover installed skills (including renamed ones)

Skills can be installed under a **custom name** (\`canary skill install <name> --name <as>\`),
so do not rely only on the canonical \`installedPaths\` below. List every \`SKILL.md\`
under your agent's skills directory — both project and personal:

    .claude/skills/*/SKILL.md   and  ~/.claude/skills/*/SKILL.md     (claude)
    .codex/skills/*/SKILL.md    and  ~/.codex/skills/*/SKILL.md      (codex)
    .github/skills/*/SKILL.md   and  ~/.copilot/skills/*/SKILL.md    (copilot)

For each file, read its frontmatter and resolve which manifest skill it came from:
- If a \`skill-source: <key>\` line is present, that \`<key>\` is the template name
  (the skill was installed under a custom \`--name\`). The install directory is the
  custom name; remember it for Step 5.
- Otherwise the \`name:\` value is the template name (a default install, where the
  install name equals the template key).

Match that resolved key against the manifest \`name\` field. Ignore any \`SKILL.md\`
whose resolved key is not in the manifest (it isn't a Canary skill).

## Step 2 — Read the version of each installed skill

The \`SKILL.md\` frontmatter contains a \`version: X.Y.Z\` line. Parse that value.
If the frontmatter has no \`version:\` field, treat the installed version as \`0.0.0\`.

## Step 3 — Compare semver

For each installed skill, compare \`installedVersion\` to the \`version\` in the manifest:
- If \`installedVersion < manifestVersion\` → the skill is **outdated**, update it.
- If equal or greater → leave it alone.

## Step 4 — For each outdated skill, inspect what changed

Before overwriting, read the diff so you know what's new. Diff by the **template
key** you resolved in Step 1 (not the custom install name) — the diff compares
source renders and is independent of how the skill was named on disk:

    canary skill diff <templateKey> --from <installedVersion>

The CLI retains past renders of each skill in \`previousVersions\`, so this prints a
unified diff from the user's installed version → the current version. Only versions
listed in \`knownVersions\` in the manifest can be diffed; if \`installedVersion\` is
older than the oldest known version, diff against the oldest known version instead
and warn the user that the older content is no longer reproducible.

Use the diff to:
- Summarize what changed for the user (new sections, removed guidance, renamed commands).
- Decide whether any user-local edits to the installed SKILL.md would be clobbered —
  if the user appears to have edited their local copy, surface that and ask before
  overwriting.

## Step 5 — Update outdated skills

Re-install with the template key, preserving both the location AND the custom name
it was installed under (the install directory name from Step 1):

    canary skill install <templateKey>                              # default-named project install
    canary skill install <templateKey> --personal                   # default-named personal install
    canary skill install <templateKey> --name <installDir>          # custom-named project install
    canary skill install <templateKey> --name <installDir> --personal

Preserve whichever location and name it was already installed in — if it lived in
\`canary-browser-testing/\`, re-install with \`--name canary-browser-testing\` so the
update lands in place rather than creating a second default-named copy. Do NOT install
skills that weren't already installed — sync only updates existing installs. When the
user has a naming convention, keep installing under their chosen names on their behalf.

## Step 6 — Report

Print a concise summary: which skills were updated (old → new version), which were
already current, and which were not installed (skipped).

## Manifest

\`\`\`json
${JSON.stringify({ skills: manifest }, null, 2)}
\`\`\`
`;

  process.stdout.write(instructions);
}

function parseInstallArgs(argv: string[]): {
  /** The registry/template key (positional argument). */
  templateName: string | undefined;
  /** Directory + frontmatter name to install under (defaults to templateName). */
  installName: string | undefined;
  agent: AgentType | undefined;
  personal: boolean;
  force: boolean;
} {
  let templateName: string | undefined;
  let installName: string | undefined;
  let agent: AgentType | undefined;
  let personal = false;
  let force = false;

  for (let i = 0; i < argv.length; i++) {
    const arg = argv[i];
    if (arg === "--agent" && i + 1 < argv.length) {
      const val = argv[++i];
      if (!isAgentType(val)) {
        console.error(`Unknown agent "${val}". Supported agents: ${SUPPORTED_AGENTS.join(", ")}`);
        process.exit(1);
      }
      agent = val;
    } else if (arg === "--name" && i + 1 < argv.length) {
      const val = argv[++i];
      if (!isValidInstallName(val)) {
        console.error(
          `Invalid skill name "${val}". Use letters, digits, hyphens, or underscores (e.g. canary-browser-testing).`
        );
        process.exit(1);
      }
      installName = val;
    } else if (arg === "--personal") {
      personal = true;
    } else if (arg === "--force" || arg === "-f") {
      force = true;
    } else if (!arg.startsWith("-") && !templateName) {
      templateName = arg;
    }
  }

  return { templateName, installName: installName ?? templateName, agent, personal, force };
}

async function runInstall(argv: string[]) {
  const {
    templateName,
    installName,
    agent: explicitAgent,
    personal,
    force,
  } = parseInstallArgs(argv);

  if (!templateName || !installName) {
    console.error("Missing skill name.\n");
    console.error(
      `Usage: canary skill install <name> [--name <install-name>] [--agent ${SUPPORTED_AGENTS.join("|")}] [--personal] [--force]`
    );
    process.exit(1);
  }

  const template = SKILL_TEMPLATES[templateName];
  if (!template) {
    console.error(`Unknown skill template "${templateName}".\n`);
    console.error("Available templates:");
    for (const [key, t] of Object.entries(SKILL_TEMPLATES)) {
      console.error(`  ${key.padEnd(24)} ${t.description}`);
    }
    process.exit(1);
  }

  // Resolve agent type
  const agent = explicitAgent ?? detectAgent();
  if (!agent) {
    console.error("Could not auto-detect the AI agent. Use --agent to specify:\n");
    console.error("  canary skill install <name> --agent claude    # Claude Code");
    console.error("  canary skill install <name> --agent codex     # OpenAI Codex");
    console.error("  canary skill install <name> --agent copilot   # GitHub Copilot");
    process.exit(1);
  }

  const dir = resolveInstallDir(installName, agent, personal);
  const filePath = join(dir, "SKILL.md");

  // Collision guard: refuse to clobber a SKILL.md installed under this name from
  // a DIFFERENT template (e.g. collapsing multiple skills onto one name) unless
  // --force is passed. Updating the same skill in place is always allowed.
  if (existsSync(filePath) && !force) {
    const existingSource = installedSourceKey(readFileSync(filePath, "utf-8"));
    if (existingSource && existingSource !== templateName) {
      console.error(
        `✗ ${installName} already holds '${existingSource}'. Pass --force to overwrite.`
      );
      process.exit(1);
    }
  }

  // Write the file (brand-aware: cofactor binary writes cofactor-branded text),
  // rewriting the frontmatter identity when installing under a custom name.
  const content = applyInstallIdentity(renderForActiveBrand(template), installName, templateName);
  try {
    mkdirSync(dir, { recursive: true });
    writeFileSync(filePath, content, "utf-8");
  } catch (error) {
    console.error(
      formatInstallWriteFailure({
        cwd: process.cwd(),
        templateName,
        installName,
        agent,
        personal,
        dir,
        filePath,
        error,
      })
    );
    process.exit(1);
  }

  const relPath = personal ? filePath : filePath.replace(process.cwd() + "/", "");
  const named = installName === templateName ? "" : ` as ${installName}`;
  console.log(`Installed ${templateName} skill for ${agent}${named} → ${relPath}`);
}

export async function runSkill(argv: string[]) {
  const [sub] = argv;

  if (!sub || sub === "help" || sub === "--help" || sub === "-h" || sub === "list") {
    printSkillHelp();
    return;
  }

  // canary skill install <name> [--agent ...]
  if (sub === "install") {
    await runInstall(argv.slice(1));
    return;
  }

  // canary skill sync — print instructions for the agent
  if (sub === "sync") {
    runSync();
    return;
  }

  if (sub === "diff") {
    runDiff(argv.slice(1));
    return;
  }

  if (sub === "versions") {
    runVersions(argv.slice(1));
    return;
  }

  // canary skill <name> — output to stdout (backward compatible)
  const template = SKILL_TEMPLATES[sub];
  if (!template) {
    console.error(`Unknown skill template "${sub}".\n`);
    printSkillHelp();
    process.exit(1);
  }

  process.stdout.write(renderForActiveBrand(template));
}
